// src/components/asset/AssetHeader.tsx

import { Box, Typography, IconButton, Tooltip, useTheme, useMediaQuery, Menu, MenuItem, ListItemIcon } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from '@mui/icons-material/MoreVert'; // The "kebab" menu icon
import RoleBasedAccess from "../../services/RoleBasedAccess";
import { downloadFilesAsync } from "../../services/FileDownloader";
import { useAsset } from "../../context/AssetContext";
import React, { useState } from "react"; // Import React and useState

const AssetHeader: React.FC = () => {
  const theme = useTheme();
  // 👇 1. Detect if we are on a mobile screen
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { asset, openEditAssetIdModal, openEditAssetModal } = useAsset();

  // 👇 2. State for the mobile actions menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (!asset) return null;

  return (
    <Box
      sx={{
        borderBottom: `solid 5px ${theme.palette.divider}`,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        // 👇 3. Use space-between on mobile for a simpler layout
        justifyContent: isMobile ? 'space-between' : 'center',
        position: "relative", // Keep relative for desktop's absolute positioning
      }}
    >
      <Box sx={{ width: isMobile ? '40px' : '120px', display: 'flex' }} />

      {/* --- ASSET ID (VIN) --- */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant={isMobile ? "h5" : "h4"} noWrap>
          {asset.vin}
        </Typography>

        {/* This "Edit ID" button is only shown on desktop, we'll move it into the menu on mobile */}
        {!isMobile && (
          <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
            <Tooltip title="Edit Asset ID">
              <IconButton onClick={openEditAssetIdModal}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          </RoleBasedAccess>
        )}
      </Box>

      {/* 👇 4. Conditionally render the action icons */}
      {isMobile ? (
        // --- MOBILE ACTIONS MENU ---
        <>
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
          >
            {/* We can use RoleBasedAccess INSIDE the menu */}
            <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
              <MenuItem onClick={() => { openEditAssetIdModal(); handleMenuClose(); }}>
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                Edit Asset ID
              </MenuItem>
            </RoleBasedAccess>
            <RoleBasedAccess allowedRoles={["Admin", "Support", "Auditor"]}>
               <MenuItem onClick={() => { openEditAssetModal(); handleMenuClose(); }}>
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                Edit Vehicle
              </MenuItem>
            </RoleBasedAccess>
            <MenuItem onClick={() => { downloadFilesAsync(asset); handleMenuClose(); }}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              Download Images
            </MenuItem>
          </Menu>
        </>
      ) : (
        // --- DESKTOP ACTIONS (Unchanged) ---
        <Box
          sx={{
            position: "absolute",
            right: 16,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}
        >
          <RoleBasedAccess allowedRoles={["Admin", "Support", "Auditor"]}>
            <Tooltip title="Edit Vehicle">
              <IconButton sx={{ backgroundColor: theme.palette.primary.main }} onClick={openEditAssetModal}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          </RoleBasedAccess>

          <Tooltip title="Download Images">
            <IconButton sx={{ backgroundColor: theme.palette.error.main }} onClick={() => downloadFilesAsync(asset)}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default AssetHeader;